export const ACCOUNT_TYPES = ['business', 'individual'] as const;

export type ISignupUserAccountType = (typeof ACCOUNT_TYPES)[number] | undefined;

/**
 * Reads an account type out of one of the systems a signup session can be hydrated from.
 *
 * Okta holds it in `userType` and Nexudus in `Custom1`, both free text, and around a quarter of
 * Nexudus coworkers - almost all of them created in 2023, before this service owned the writes -
 * hold "Business" or "Individual". Every comparison downstream is against the lower-case pair and
 * the signup step schema rejects anything else, so a fixup session seeded from those systems fails
 * its first submit on a field the user was never shown.
 *
 * Anything that is neither reads as absent, which puts the user on the billing step's account-type
 * question rather than into a step they have no way out of.
 */
export function parseAccountType(value: unknown): ISignupUserAccountType {
  const normalised = typeof value === 'string' ? value.trim().toLowerCase() : undefined;
  return ACCOUNT_TYPES.find(accountType => accountType === normalised);
}

/**
 * Contains details of a pending signup request
 *
 * IMPORTANT: If you update this make sure you also update the Mongoose schema in companion-auth/src/utils/schemas/sessionsSchema.ts
 */
export default interface ISignupUser {
  accountType?: ISignupUserAccountType;
  homeLocation?: number;
  name?: string;
  salutation?: string;
  phone?: string;
  email?: string;
  businessName?: string; //for business account
  companyNumber?: string; //for business account
  tradingName?: string; // for individual account
  address?: string;
  address2?: string;
  state?: string;
  postCode?: string;
  city?: string;
  vatNumber?: string;
  terms?: boolean;
  hasMarketingConsent?: boolean;
  oktaId?: string;
  isFixingUp?: boolean;
  /**
   * Set when the billing step sends the verification email, i.e. the flow has moved on to the
   * verify step. Server-authoritative: the detail steps close behind it, so a user who leaves at
   * the code entry screen resumes there rather than at step 1.
   */
  hasStartedVerification?: boolean;
  /**
   * Set by resumeVerification for a staged account that never completed billing. The session
   * deliberately carries no name/phone/location - anyone who knows the email can reach this
   * point, so nothing from the stored profile is exposed - and the personal step is reopened so
   * the user re-enters them. Dropped on the next step submit, once the details exist.
   */
  activationNeedsPersonalDetails?: boolean;
}
